import { toJS } from "mobx";
import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { NumberToMonetaryStringFormat } from "src/common/Methods/FileUtil";
import { AbstractLineItem, Invoice } from "src/model/Financial";
import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "src/stores/CustomerStore";

import { faCashRegister, faChevronDown, faChevronUp, faPrint } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as fileUtil from "../../common/Methods/FileUtil";
import { Disbursement, Payment, Proceeds } from "../../model/Financial";
import { HorseForSale, SimpleHorseForSale } from "../../model/HorseForSale";
import { FormDialog } from "../common/FormDialog";
import { HorseForSaleLink } from "../common/HorseForSaleLink";
import LoadingWidget from "../common/LoadingWidget";
import { DateGroup } from "../common/SimpleFormComponents";
import { errorMessage } from "../common/Toast";
import SettlementDialog from "./SettlementDialog";

export interface State {
    wasValidated: boolean;
    expanded: Array<number>;
    showSettlementDialog: boolean;
    // Statement related
    showPrintDialog: boolean;
    dateFrom: string;
    dateTo: string;
}

export interface Props {
    customerStore?: CustomerStore;
    configurationStore?: ConfigurationStore;
}

@inject("customerStore")
@inject("configurationStore")
@observer
export default class StatementComponent extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            showPrintDialog: false,
            wasValidated: false,
            showSettlementDialog: false,
            dateFrom: "",
            dateTo: "",
            expanded: []
        };
    }

    closeSettlementDialog = () => {
        this.setState({ showSettlementDialog: false });
    };

    closePrintDialog = () => {
        this.setState({ showPrintDialog: false });
    };

    submit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.form && this.form.checkValidity() && this.state.dateFrom && this.state.dateTo) {
            this.setState({ wasValidated: false });
            this.printStatement();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    // DOUBLE CHECK FROM TO TO FORM
    printStatement = async () => {
        try {
            const dateFrom = moment(this.state.dateFrom).format("YYYY-MM-DD");
            const dateTo = moment(this.state.dateTo).format("YYYY-MM-DD");

            const blob = await this.props.customerStore!.printStatement(dateFrom, dateTo);
            fileUtil.ShowBlobOnNewtab("Statement_of_Account.pdf", blob);
            this.closePrintDialog();
        } catch (error) {
            errorMessage(error);
        }
    };

    handleOnExpand = (row: any, isExpand: boolean) => {
        if (isExpand) {
            this.setState(() => ({
                expanded: [...this.state.expanded, row.id]
            }));
        } else {
            this.setState(() => ({
                expanded: this.state.expanded.filter(x => x !== row.id)
            }));
        }
    };

    addType = (type: string) => (obj: any) => {
        obj = JSON.parse(JSON.stringify(obj));
        obj.type = type;
        return obj;
    };

    extractHorsesForSaleFromRow = (row: any): Array<SimpleHorseForSale> => {
        const sortFn = (a: HorseForSale, b: HorseForSale) => (a.hipNumber || a.id) - (b.hipNumber || b.id);
        if (row.type === "Invoice" && (row as Invoice).horseForSaleTag !== null) {
            return [(row as Invoice).horseForSaleTag!];
        } else if (row.type === "Proceeds" && (row as Proceeds).fromHorseForSale !== null) {
            return [(row as Proceeds).fromHorseForSale!];
        } else if (row.type === "Disbursement") {
            return ((row as Disbursement).horsesForSale || []).sort(sortFn);
        } else if (row.type === "Payment") {
            return ((row as Payment).horsesForSale || []).sort(sortFn);
        } else {
            return [];
        }
    };

    render() {
        const { configurationStore } = this.props;
        if (!configurationStore) {
            throw new Error(" ** No Configuration Store **");
        }

        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            return null;
        }

        const account = customer.account;
        const balance = account.balance;

        const data = [
            ...account.account.invoices.filter(i => i.dateVoided === null).map(this.addType("Invoice")),
            ...account.account.payments.filter(p => p.dateArchived === null).map(this.addType("Payment")),
            ...account.account.proceeds.filter(p => p.dateArchived === null).map(this.addType("Proceeds")),
            ...account.account.disbursements.filter(d => d.dateArchived === null).map(this.addType("Disbursement"))
        ]
            .filter((a: AbstractLineItem | Invoice) => (a as Invoice).dateVoided === null || (a as AbstractLineItem).entryDate > 0)
            .sort((a: AbstractLineItem | Invoice, b: AbstractLineItem | Invoice) => {
                if ((a as Invoice).issueDate && (b as Invoice).issueDate) {
                    return (b as Invoice).issueDate - (a as Invoice).issueDate;
                } else if ((a as AbstractLineItem).entryDate && (b as Invoice).issueDate) {
                    return (b as Invoice).issueDate - (a as AbstractLineItem).entryDate;
                } else if ((a as Invoice).issueDate && (b as AbstractLineItem).entryDate) {
                    return (b as AbstractLineItem).entryDate - (a as Invoice).issueDate;
                } else {
                    return (b as AbstractLineItem).entryDate - (a as AbstractLineItem).entryDate;
                }
            });

        const nonExpand = data.map(item => {
            if (
                item.type === "Payment" ||
                item.type === "Proceeds" ||
                item.type === "Disbursement" ||
                (item.type === "Invoice" && item.lineItems.length === 0)
            ) {
                return item.id;
            }
        });

        const expandRow = {
            renderer: (row: Invoice) =>
                row.lineItems.length > 0 ? (
                    <div className="mx-0 my-0 px-0 py-0">
                        {row.lineItems.map((lineItem, index) => {
                            return (
                                <div
                                    key={lineItem.id}
                                    className={`row mx-0 px-0 py-1`}
                                    style={index !== row.lineItems.length - 1 ? { borderBottom: "1px solid #ddd" } : {}}
                                >
                                    <div className="col-4 px-0">
                                        {lineItem.entryType.id
                                            ? configurationStore.entryTypes.filter(e => e.id === lineItem.entryType.id)[0].name
                                            : "-"}
                                    </div>
                                    <div className="col-4 mx-0 px-0">{lineItem.description}</div>
                                    <div className="col-4 mx-0 px-0 text-right">${NumberToMonetaryStringFormat(lineItem.amountInCents)}</div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div />
                ),
            expanded: this.state.expanded,
            onExpand: this.handleOnExpand,
            nonExpandable: nonExpand,
            showExpandColumn: true,
            expandHeaderColumnRenderer: () => null,
            expandColumnRenderer: (e: any) => {
                if (nonExpand.find(item => item === e.rowKey)) {
                    return <span />;
                }
                return e.expanded ? <FontAwesomeIcon icon={faChevronUp} size="xs" /> : <FontAwesomeIcon icon={faChevronDown} size="xs" />;
            },
            expandColumnPosition: "right"
        };

        const columns = [
            {
                dataField: "entryDate",
                text: "Transaction \n Date",
                formatter: (cellContent: any, row: any) => {
                    return row.entryDate
                        ? moment(row.entryDate).format("YYYY/MM/DD")
                        : row.issueDate
                        ? moment(row.issueDate).format("YYYY/MM/DD")
                        : "-";
                },
                sort: true,
                sortFunc: (_: any, __: any, order: "asc" | "desc", field: string, a: any, b: any) => {
                    const aPrime = a.entryDate || a.issueDate;
                    const bPrime = b.entryDate || b.issueDate;
                    return order === "asc" ? aPrime - bPrime : bPrime - aPrime;
                }
            },
            {
                dataField: "invoiceId",
                text: "Invoice #",
                formatter: (cellContent: any, row: any) => (row.type === "Invoice" ? (row as Invoice).id : "-")
            },
            {
                dataField: "horseForSaleId",
                text: "Horse for Sale(s)",
                formatter: (cellContent: any, row: any) => {
                    return this.extractHorsesForSaleFromRow(row).map(hfs => <HorseForSaleLink key={hfs.id} horseForSale={hfs} targetBlank={true} />);
                },
                sort: true,
                sortFunc: (_: any, __: any, order: "asc" | "desc", field: string, a: any, b: any) => {
                    const hfsA = this.extractHorsesForSaleFromRow(a);
                    const hfsB = this.extractHorsesForSaleFromRow(b);
                    const aPrime = hfsA[0] ? hfsA[0].sale.id * 1000000 + (hfsA[0].hipNumber || hfsA[0].id) : 0;
                    const bPrime = hfsB[0] ? hfsB[0].sale.id * 1000000 + (hfsB[0].hipNumber || hfsB[0].id) : 0;
                    return order === "asc" ? aPrime - bPrime : bPrime - aPrime;
                }
            },
            {
                dataField: "type",
                text: "Type"
            },
            {
                dataField: "description",
                text: "Description",
                formatter: (cellContent: any, row: AbstractLineItem) => (row.description ? row.description : "")
            },
            {
                dataField: "debit",
                text: "Charges & \n Disbursements",
                style: { textAlign: "right" },
                headerStyle: { textAlign: "right" },
                formatter: (cellContent: any, row: AbstractLineItem | any) => {
                    if (row.type === "Disbursement") {
                        return fileUtil.CovertAndFormatCentsToDollarsWithDollarSign(row.amountInCents);
                    } else if (row.type === "Invoice" && row.total > 0) {
                        return fileUtil.CovertAndFormatCentsToDollarsWithDollarSign(row.total);
                    } else {
                        return "-";
                    }
                }
            },
            {
                dataField: "credit",
                text: "Credits & \n Payments",
                style: { textAlign: "right" },
                headerStyle: { textAlign: "right" },
                formatter: (cellContent: any, row: AbstractLineItem | any) => {
                    if (row.type === "Payment" || row.type === "Proceeds") {
                        return fileUtil.CovertAndFormatCentsToDollarsWithDollarSign(row.amountInCents);
                    } else if (row.type === "Invoice" && row.total < 0) {
                        return fileUtil.CovertAndFormatCentsToDollarsWithDollarSign(Math.abs(row.total));
                    } else {
                        return "-";
                    }
                }
            }
        ];

        return (
            <>
                <div className="card shadow mb-5">
                    <div className="card-header">Statement of Account</div>
                    <div className="card-body">
                        <div className="row mr-0 mb-3 d-flex">
                            <div className="pl-3">
                                <button
                                    className="btn btn-primary btn-sm mr-2 mb-1"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.setState({ showSettlementDialog: true });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCashRegister} className="mr-2" />
                                    Create a Settlement
                                </button>
                                <button
                                    className="btn btn-primary btn-sm mb-1"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.setState({ showPrintDialog: true });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPrint} className="mr-2" />
                                    Print Statement of Account
                                </button>
                            </div>
                            <div className="ml-auto pr-0 pt-2">
                                Balance: <span className="text-info">{fileUtil.CovertAndFormatCentsToDollarsWithDollarSign(balance)}</span>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <BootstrapTable
                                keyField={"id"}
                                remote={false}
                                hover={true}
                                bordered={false}
                                columns={columns}
                                bootstrap4={true}
                                data={data}
                                classes="table table-sm expansion-table"
                                // noDataIndication={() => <div><span>No results found.</span></div>}
                                pagination={paginationFactory()}
                                expandRow={expandRow}
                                noDataIndication={() =>
                                    customerStore.loading ? (
                                        <LoadingWidget />
                                    ) : (
                                        <div>
                                            <span>No results found.</span>
                                        </div>
                                    )
                                }
                            />
                            {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                    Otherwise the `noDataIndication` wouldn't work */}
                            {customerStore.loading && <div />}
                        </div>
                    </div>
                </div>
                <SettlementDialog showSettlementDialog={this.state.showSettlementDialog} closeDialog={this.closeSettlementDialog} />
                <FormDialog
                    title="Select From and To dates"
                    modalSize="modal-md"
                    show={this.state.showPrintDialog}
                    onClose={() => this.closePrintDialog()}
                >
                    <form
                        className={"px-2 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                        noValidate={true}
                        onSubmit={this.submit}
                        ref={ref => (this.form = ref)}
                    >
                        <div className="form-row">
                            <div className="col-12 pr-3">
                                <div className="form-row">
                                    <DateGroup
                                        label="From:"
                                        invalidFeedBack="Please provide a date."
                                        name="dateFrom"
                                        className="col-sm-12 col-md-6 mb-0"
                                        inputClassName="not-required"
                                        value={this.state.dateFrom}
                                        disabled={false}
                                        onChange={e => this.setState({ dateFrom: e })}
                                        isRequired={true}
                                        icon={true}
                                        dateFormat="YYYY/MM/DD"
                                    />
                                    <DateGroup
                                        label="To:"
                                        invalidFeedBack="Please provide a date."
                                        name="dateTo"
                                        className="col-sm-12 col-md-6 mb-0"
                                        inputClassName="not-required"
                                        value={this.state.dateTo}
                                        disabled={false}
                                        onChange={e => this.setState({ dateTo: e })}
                                        isRequired={true}
                                        icon={true}
                                        dateFormat="YYYY/MM/DD"
                                    />
                                </div>
                                <div className="form-row text-right">
                                    <button type="button" className="btn btn-secondary mr-auto" onClick={() => this.closePrintDialog()}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        Print
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </FormDialog>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/StatementsComponent.tsx